import { includes } from 'ramda';

import { type FullParams } from '@/app/urls/url-mapping';
import {
  type DatoBlogCategory,
  type DatoListArticle,
} from '@/lib/datocms/types';

export type ArticleListData<ArticleAlias extends string = 'allBlogArticles'> = {
  [Alias in ArticleAlias]: DatoListArticle[];
} & {
  allBlogCategories: DatoBlogCategory[];
  articleCount: { count: number };
};

export const listArticleFragment = `
  fragment listArticleFragment on BlogArticleRecord {
    author {
      name
    }
    body {
      value
    }
    coverImage {
      responsiveImage(
        imgixParams: {
          auto: format
          crop: left
          fit: crop
          h: 160
          q: 45
          w: 363
        }
      ) {
        ...responsiveImageFragment
      }
    }
    publicationDate
    readingTime
    series {
      name
    }
    slug
    subtitle
    title
  }
`;

export const getArticleListQueryFields = (
  options?: Partial<{
    articleAlias: string;
    // If filterMode isn't sent, this won't filter by category at all.
    filterMode: 'and' | 'or';
    includeAllCategories: boolean;
  }>
): string => {
  const { articleAlias, filterMode, includeAllCategories } = {
    includeAllCategories: false,
    ...options,
  };
  const filterKey = filterMode === 'or' ? 'anyIn' : 'allIn';
  return `
    ${articleAlias ? `${articleAlias}: ` : ''}allBlogArticles(
      filter: {
        ${filterMode ? `categories: { ${filterKey}: $categories },` : ''}
        regions: { anyIn: [$regionId] },
      },
      first: $pageSize,
      locale: $locale,
      orderBy: publicationDate_DESC,
      skip: $skip,
    ) {
      ...listArticleFragment
    }
    ${
      includeAllCategories
        ? `allBlogCategories(locale: $locale) {
             categoryType
             code
             name
           }`
        : ''
    }
    articleCount: _allBlogArticlesMeta(
      filter: {
        ${filterMode ? `categories: { ${filterKey}: $categories },` : ''}
        regions: { anyIn: [$regionId] },
      },
      locale: $locale,
    ) {
      count
    }
  `;
};

export const updateBlogHomeRegion = (params: FullParams): FullParams => {
  const { language, region } = params;
  return language === 'en' && !includes(region, ['us', 'uk', 'ww'])
    ? // Spread params in case there are other properties in there
      { ...params, region: 'us' }
    : params;
};
